// Fonte ÚNICA de validação/normalização do valor de registro
// manual de parâmetro, consumida pelo service da API e pelo campo do frontend
// (guardrail #5: regra cross-wire não pode divergir entre FE e BE).

import type { ParameterCategory } from "../api-contracts/parameters.js";

export type ParameterEntryValueReason =
  | "INVALID_NUMERIC"
  | "INVALID_COUNTER"
  | "INVALID_BOOLEAN"
  | "INVALID_DURATION"
  | "INVALID_TEXT"
  | "STATUS_NOT_SUPPORTED";

export type ParameterEntryValueResult =
  | { ok: true; value: string }
  | { ok: false; reason: ParameterEntryValueReason; message: string };

// Aceita decimais com vírgula pt-BR e sem zero à esquerda (",5" → "0.5").
const NUMERIC_RE = /^-?(\d+(\.\d+)?|\.\d+)$/;
// Spec 081 (FR-027) — o contador deixou de contar só peça inteira. Uma linha que mede em quilo lê
// 415,5, e `^\d+$` recusava exatamente o caso que a feature existe para permitir — enquanto o mesmo
// contador alimentado por sensor aceitava a fração, fazendo os dois caminhos discordarem sobre o
// que é uma leitura válida. Não-negativo continua valendo.
const COUNTER_RE = /^(\d+(\.\d+)?|\.\d+)$/;
const DURATION_RE = /^(\d+(\.\d+)?|\.\d+)$/;

function normalizeDecimal(raw: string): string {
  let v = raw.trim().replace(",", ".");
  if (v.startsWith(".")) v = `0${v}`;
  else if (v.startsWith("-.")) v = `-0${v.slice(1)}`;
  return v;
}

export function validateParameterEntryValue(
  category: ParameterCategory,
  raw: string
): ParameterEntryValueResult {
  switch (category) {
    case "NUMERIC": {
      const value = normalizeDecimal(raw);
      if (!NUMERIC_RE.test(value) || !Number.isFinite(Number(value))) {
        return {
          ok: false,
          reason: "INVALID_NUMERIC",
          message: "Informe um número (ex.: 12,5).",
        };
      }
      return { ok: true, value };
    }
    case "COUNTER": {
      const value = normalizeDecimal(raw);
      if (!COUNTER_RE.test(value) || !Number.isFinite(Number(value))) {
        return { ok: false, reason: "INVALID_COUNTER", message: "Informe um número ≥ 0." };
      }
      // Teto de segurança: o rollup soma em `NUMERIC(14,3)` e a equivalência ainda multiplica o
      // valor lido, então um número enorme estoura a coluna com `numeric field overflow` — 500 em
      // vez de 4xx. O limite abaixo é conservador de propósito.
      if (Number(value) > 2147483647) {
        return {
          ok: false,
          reason: "INVALID_COUNTER",
          message: "Valor muito alto (máximo 2.147.483.647).",
        };
      }
      return { ok: true, value };
    }
    case "BOOLEAN": {
      const value = raw.trim();
      if (value !== "true" && value !== "false") {
        return {
          ok: false,
          reason: "INVALID_BOOLEAN",
          message: 'Valor booleano deve ser "true" ou "false".',
        };
      }
      return { ok: true, value };
    }
    case "DURATION": {
      const value = normalizeDecimal(raw);
      if (!DURATION_RE.test(value) || !Number.isFinite(Number(value))) {
        return { ok: false, reason: "INVALID_DURATION", message: "Informe uma duração ≥ 0." };
      }
      return { ok: true, value };
    }
    case "TEXT": {
      const value = raw.trim();
      if (value.length < 1 || value.length > 500) {
        return { ok: false, reason: "INVALID_TEXT", message: "Texto de 1 a 500 caracteres." };
      }
      return { ok: true, value };
    }
    case "STATUS":
      return {
        ok: false,
        reason: "STATUS_NOT_SUPPORTED",
        message: "Parâmetro de Status usa o registro de status.",
      };
  }
}
